import { getFirestore, collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, Timestamp, deleteDoc } from 'firebase/firestore';
import { dataApp } from '../../config/firebase';
import { productService } from '../../services/ProductService';

const db = getFirestore(dataApp);

export interface OptimizationConfig {
  model: string;
  baseUrl: string;
  maxTokens: number;
  temperature: number;
}

export interface OptimizationPrompt {
  id: string;
  marketplace: string;
  title: string;
  content: string;
  isDefault: boolean;
  status?: 'Active' | 'Inactive';
  lastUsed?: Date;
}

export interface OptimizationResult {
  productId: string;
  marketplace: string;
  content: {
    title: string;
    description: string;
    bulletPoints: string[];
    metaDescription?: string;
    productType?: string;
    productCategory?: string;
  };
  prompt: {
    id: string;
    title: string;
  };
  timestamp: Date;
  status: 'success' | 'error';
  error?: string;
}

export class OptimizationService {
  private static instance: OptimizationService;
  private readonly config: OptimizationConfig = {
    model: 'gpt-4',
    baseUrl: 'https://api.openai.com/v1/chat/completions',
    maxTokens: 2000,
    temperature: 0.7
  };

  protected constructor() {}

  private normalizeMarketplace(marketplace: string): 'shopify' | 'amazon' | 'googleShopping' {
    const normalized = marketplace.toLowerCase();
    switch (normalized) {
      case 'google shopping':
        return 'googleShopping';
      case 'shopify':
        return 'shopify';
      case 'amazon':
        return 'amazon';
      default:
        throw new Error(`Invalid marketplace: ${marketplace}`);
    }
  }

  private denormalizeMarketplace(marketplace: 'shopify' | 'amazon' | 'googleShopping'): string {
    switch (marketplace) {
      case 'googleShopping':
        return 'Google Shopping';
      case 'shopify':
        return 'Shopify';
      case 'amazon':
        return 'Amazon';
      default:
        return marketplace;
    }
  }

  static getInstance(): OptimizationService {
    if (!OptimizationService.instance) {
      OptimizationService.instance = new OptimizationService();
    }
    return OptimizationService.instance;
  }

  private async getOpenAIKey(): Promise<string> {
    const configDoc = await getDoc(doc(db, 'store_configs', 'openai'));
    const config = configDoc.data();
    if (!config?.apiKey) {
      throw new Error('OpenAI API key not found');
    }
    return config.apiKey;
  }

  private async callOpenAI(prompt: string, marketplace: string): Promise<any> {
    const apiKey = await this.getOpenAIKey();

    

    const systemMessage = marketplace === 'google shopping' && prompt.includes('CRITICAL INSTRUCTIONS')
      ? `You are a product listing optimization expert for ${marketplace}. Always provide exact matches from the Google Shopping taxonomy.`
      : `You are a product listing optimization expert for ${marketplace}. Always respond with properly formatted JSON following the exact structure specified in the prompt.`;

    const response = await fetch(this.config.baseUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: this.config.model,
        messages: [
          {
            role: 'system',
            content: systemMessage
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: this.config.temperature,
        max_tokens: this.config.maxTokens
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    if (!data.choices?.[0]?.message?.content) {
      throw new Error('Invalid API response structure');
    }

    const content = data.choices[0].message.content;

    // If this is a category determination request, return the raw content
    if (marketplace === 'google shopping' && prompt.includes('CRITICAL INSTRUCTIONS')) {
      return content;
    }

    // For optimization requests, parse the JSON response
    try {
      return JSON.parse(content);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to parse optimization response: ${error.message}`);
      }
      throw new Error('Failed to parse optimization response');
    }
  }

  private validateOptimizationResponse(content: any, marketplace: string, product?: any): any {
    if (!content) throw new Error('No optimization content received');

    const validators = {
      amazon: (content: Record<string, any>) => {
        if (!content.title || !content.description || !content.bulletPoints) {
          throw new Error('Missing required Amazon optimization fields');
        }
        if (content.title.length > 200) {
          throw new Error('Amazon title exceeds 200 characters');
        }

        // Ensure bulletPoints is an array
        if (typeof content.bulletPoints === 'string') {
          content.bulletPoints = content.bulletPoints.split('\n').filter(Boolean);
        }

        if (!Array.isArray(content.bulletPoints)) {
          throw new Error('Bullet points must be an array or newline-separated string');
        }

        // Pad or trim bullet points to exactly 5
        if (content.bulletPoints.length < 5) {
          while (content.bulletPoints.length < 5) {
            content.bulletPoints.push('FEATURE: Additional product feature');
          }
        } else if (content.bulletPoints.length > 5) {
          content.bulletPoints = content.bulletPoints.slice(0, 5);
        }

        return content;
      },

      shopify: (content: Record<string, any>) => {
        const missingFields = [];
        if (!content.title) missingFields.push('title');
        if (!content.description) missingFields.push('description');
        if (!content.bulletPoints) missingFields.push('bulletPoints');
        if (!content.metaDescription) missingFields.push('metaDescription');

        if (missingFields.length > 0) {
          throw new Error(`Missing required Shopify fields: ${missingFields.join(', ')}`);
        }

        if (content.title.length > 150) {
          throw new Error(`Shopify title exceeds 150 characters (current: ${content.title.length})`);
        }

        if (content.metaDescription.length > 160) {
          content.metaDescription = content.metaDescription.substring(0, 157) + '...';
        }
        if (content.metaDescription.length < 50) {
          throw new Error(`Shopify meta description must be at least 50 characters (current: ${content.metaDescription.length})`);
        }

        if (typeof content.bulletPoints === 'string') {
          content.bulletPoints = content.bulletPoints.split('\n').filter(Boolean);
        }

        if (!Array.isArray(content.bulletPoints)) {
          throw new Error('Bullet points must be an array or newline-separated string');
        }

        // Ensure exactly 6 bullet points
        if (content.bulletPoints.length !== 6) {
          throw new Error('Must have exactly 6 bullet points');
        }

        // Validate bullet points format
        const secondPoint = content.bulletPoints[1];

        // Check for duplicate PRODUCT SPECIFICATIONS
        const specPoints = content.bulletPoints.filter(point => 
          point.toUpperCase().includes('PRODUCT SPECIFICATIONS') || 
          point.toUpperCase().includes('SPECIFICATIONS:')
        );

        if (specPoints.length > 1) {
          throw new Error('Product specifications must only appear in the second bullet point');
        }

        // Ensure second point has specifications
        if (!secondPoint.toUpperCase().includes('PRODUCT SPECIFICATIONS') && 
            !secondPoint.toUpperCase().includes('SPECIFICATIONS:')) {
          // Extract specs from title
          const dimensions = content.title.match(/\d+(?:"|''|inch|in)?/);
          const quantity = content.title.match(/\d+\s*(?:pack|piece|pc|pcs|set)/i);
          
          // Create comprehensive specifications
          const specs = [];
          if (quantity) specs.push(quantity[0]);
          if (dimensions) specs.push(`${dimensions[0]} size`);
          
          // Add any additional specs from the title
          const materials = content.title.match(/(?:made of|from)\s+([^,;.]+)/i);
          if (materials) specs.push(materials[1]);
          
          const specsBullet = `PRODUCT SPECIFICATIONS: ${specs.join(', ')} - premium quality product designed for professional use`.trim();
          content.bulletPoints[1] = specsBullet;
        }

        // Define the exact lifetime guarantee format
        const guaranteeFormat = `GUARANTEED FOR LIFE: We offer a No Nonsense Lifetime Satisfaction Guarantee on all of our ${product?.basicInfo?.type || 'products'}. If at any point you're not 100% happy, just send us an email, and we promise to make it right!`;
        
        // Ensure sixth point matches the exact format
        if (content.bulletPoints[5] !== guaranteeFormat) {
          content.bulletPoints[5] = guaranteeFormat;
        }

        // Generate page title if none exists or if it matches the product title
        if (!content.pageTitle || content.pageTitle === content.title) {
          const productName = content.title.split(' - ')[0].trim();
          const brand = 'Avant Grub';
          
          // Extract key features for the keyword
          const features = content.title.match(/(?:with|for|featuring)\s+([^,;.]+)/i);
          const keyword = features ? features[1] : content.title.split(' ').slice(-2).join(' ');
          
          content.pageTitle = `${productName} | ${brand} | ${keyword}`.substring(0, 60);
        }

        return content;
      },

      'google shopping': (content: Record<string, any>) => {
        if (!content.title || !content.description || !content.bulletPoints) {
          throw new Error('Missing required Google Shopping optimization fields');
        }
        if (content.title.length > 150) {
          throw new Error('Google Shopping title exceeds 150 characters');
        }

        if (typeof content.bulletPoints === 'string') {
          content.bulletPoints = content.bulletPoints.split('\n').filter(Boolean);
        }

        if (!Array.isArray(content.bulletPoints)) {
          throw new Error('Bullet points must be an array or newline-separated string');
        }

        const formattedBulletPoints = content.bulletPoints
          .map((point: string) => point.trim())
          .filter((point: string) => Boolean(point))
          .map((point: string) => `"${point}"`);

        return {
          ...content,
          bulletPoints: formattedBulletPoints
        };
      }
    };

    const validatorKey = marketplace.toLowerCase();
    const validator = validators[validatorKey as keyof typeof validators];
    if (!validator) {
      throw new Error(`No validator found for marketplace: ${marketplace}`);
    }

    return validator(content);
  }

  async getPromptById(promptId: string): Promise<OptimizationPrompt | null> {
    try {
      
      const promptDoc = await getDoc(doc(db, 'optimization_prompts', promptId));
      
      if (promptDoc.exists()) {
        
        return {
          id: promptDoc.id,
          ...promptDoc.data()
        } as OptimizationPrompt;
      } else {
        
        return null;
      }
    } catch (error) {
      
      throw error;
    }
  }

  async getPrompts(marketplace: string): Promise<OptimizationPrompt[]> {
    try {
      // Special handling for Google Shopping
      const normalizedMarketplace = marketplace.toLowerCase() === 'google shopping' 
        ? 'Google Shopping'
        : marketplace.charAt(0).toUpperCase() + marketplace.slice(1).toLowerCase();
      
      
      
      // First try to get all prompts for the marketplace
      const q = query(
        collection(db, 'optimization_prompts'),
        where('marketplace', '==', normalizedMarketplace),
        orderBy('lastUsed', 'desc')
      );

      

      const querySnapshot = await getDocs(q);
      const prompts = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert Timestamp to Date if lastUsed exists
        if (data.lastUsed && typeof data.lastUsed.toDate === 'function') {
          data.lastUsed = data.lastUsed.toDate();
        }
          return {
            id: doc.id,
            ...data,
            status: data.isDefault ? 'Active' : (data.status || 'Active')
          };
      }) as OptimizationPrompt[];

      

      if (prompts.length === 0) {
        
        
        // Try to get the default prompt directly
        const defaultPromptId = `default-${normalizedMarketplace.toLowerCase().replace(/\s+/g, '-')}-prompt`;
        
        
        const defaultPromptDoc = await getDoc(doc(db, 'optimization_prompts', defaultPromptId));
        if (defaultPromptDoc.exists()) {
          const data = defaultPromptDoc.data();
          
          // Convert Timestamp to Date if lastUsed exists
          if (data.lastUsed && typeof data.lastUsed.toDate === 'function') {
            data.lastUsed = data.lastUsed.toDate();
          }
          return [{
            id: defaultPromptDoc.id,
            ...data,
            status: 'Active'
          }] as OptimizationPrompt[];
        }
      }

      return prompts;
    } catch (error) {
      
      throw error;
    }
  }

  async savePrompt(prompt: OptimizationPrompt): Promise<void> {
    try {
      const docRef = doc(collection(db, 'optimization_prompts'), prompt.id);
      // Special handling for Google Shopping
      const normalizedMarketplace = prompt.marketplace.toLowerCase() === 'google shopping' 
        ? 'Google Shopping'
        : prompt.marketplace.charAt(0).toUpperCase() + prompt.marketplace.slice(1).toLowerCase();

      await setDoc(docRef, {
        ...prompt,
        marketplace: normalizedMarketplace,
        status: prompt.isDefault ? 'Active' : (prompt.status || 'Active')
      });
    } catch (error) {
      
      throw error;
    }
  }

  async updatePromptStatus(promptId: string, status: 'Active' | 'Inactive'): Promise<void> {
    try {
      const promptRef = doc(db, 'optimization_prompts', promptId);
      const promptDoc = await getDoc(promptRef);
      
      if (!promptDoc.exists()) {
        throw new Error('Prompt not found');
      }
      
      const promptData = promptDoc.data();
      if (promptData.isDefault) {
        throw new Error('Cannot change status of default prompts');
      }

      await setDoc(promptRef, { status }, { merge: true });
    } catch (error) {
      
      throw error;
    }
  }

  async deletePrompt(promptId: string): Promise<void> {
    try {
      // First verify this is not a default prompt
      const promptDoc = await getDoc(doc(db, 'optimization_prompts', promptId));
      if (!promptDoc.exists()) {
        throw new Error('Prompt not found');
      }
      
      const promptData = promptDoc.data();
      if (promptData.isDefault) {
        throw new Error('Cannot delete default prompts');
      }

      // Delete the prompt document
      await deleteDoc(doc(db, 'optimization_prompts', promptId));
    } catch (error) {
      
      throw error;
    }
  }

  async optimizeProduct(
    productId: string,
    marketplace: string,
    promptId: string
  ): Promise<OptimizationResult> {
    // Special handling for Google Shopping
    const normalizedMarketplace = marketplace.toLowerCase() === 'google shopping' 
      ? 'Google Shopping'
      : marketplace.charAt(0).toUpperCase() + marketplace.slice(1).toLowerCase();
    try {
      // Get product data
      const productDoc = await getDoc(doc(db, 'products', productId));
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }
      const product = productDoc.data();

      // Get prompt
      const promptDoc = await getDoc(doc(collection(db, 'optimization_prompts'), promptId));
      if (!promptDoc.exists()) {
        throw new Error('Prompt not found');
      }
      const prompt = { id: promptDoc.id, ...promptDoc.data() } as OptimizationPrompt;

      // Verify prompt marketplace matches (case-insensitive)
      if (prompt.marketplace.toLowerCase() !== normalizedMarketplace.toLowerCase()) {
        throw new Error('Prompt marketplace does not match selected marketplace');
      }

      // Generate and send prompt to OpenAI
      const generatedPrompt = this.generatePrompt(product, normalizedMarketplace, prompt.content);
      const optimizedContent = await this.callOpenAI(generatedPrompt, normalizedMarketplace);

      // Validate response
      const validatedContent = this.validateOptimizationResponse(optimizedContent, normalizedMarketplace, product);

      // Create optimization result
      const marketplaceKey = this.normalizeMarketplace(normalizedMarketplace);
      const result: OptimizationResult = {
        productId,
        marketplace: this.denormalizeMarketplace(marketplaceKey),
        content: validatedContent,
        prompt: {
          id: prompt.id,
          title: prompt.title
        },
        timestamp: new Date(),
        status: 'success'
      };

      // Save optimization result
      await this.saveOptimizationResult(result);

      // Update prompt's lastUsed
      await setDoc(doc(collection(db, 'optimization_prompts'), prompt.id), {
        lastUsed: Timestamp.now()
      }, { merge: true });

      // Update product with optimized content
      await this.updateProductWithOptimization(productId, result);

      return result;
    } catch (error) {
      
      const errorResult: OptimizationResult = {
        productId,
        marketplace,
        content: {
          title: '',
          description: '',
          bulletPoints: []
        },
        prompt: {
          id: promptId || 'default',
          title: 'Unknown'
        },
        timestamp: new Date(),
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
      await this.saveOptimizationResult(errorResult);
      throw error;
    }
  }

  private async saveOptimizationResult(result: OptimizationResult): Promise<void> {
    try {
      const optimizationRef = doc(collection(db, 'optimizations'));
      await setDoc(optimizationRef, {
        ...result,
        timestamp: Timestamp.fromDate(result.timestamp)
      });
    } catch (error) {
      
      throw error;
    }
  }

  private async updateProductWithOptimization(productId: string, optimization: OptimizationResult): Promise<void> {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }

    const product = productDoc.data();
    const marketplaceKey = this.normalizeMarketplace(optimization.marketplace);
    const optimizations = product.optimizations || {};

    optimizations[marketplaceKey] = {
      ...optimization.content,
      lastOptimized: Timestamp.fromDate(optimization.timestamp)
    };

    // Use ProductService to update the product, which will create a version
    await productService.updateProduct(
      productId,
      {
        optimizations,
        timestamps: {
          ...product.timestamps,
          updated: new Date()
        }
      },
      'optimization',
      marketplaceKey
    );
  }

  async updateOptimizedContent(
    productId: string,
    marketplace: 'Shopify' | 'Google Shopping' | 'Amazon',
    field: keyof OptimizationResult['content'],
    value: string | string[]
  ): Promise<void> {
    try {
      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);
      
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }

      const product = productDoc.data();
      const marketplaceKey = this.normalizeMarketplace(marketplace);
      const optimizations = product.optimizations || {};
      const currentContent = optimizations[marketplaceKey] || {};

      // Update the specific field while preserving other fields
      optimizations[marketplaceKey] = {
        ...currentContent,
        [field]: value,
        lastOptimized: Timestamp.now()
      };

      // Use ProductService to update the product, which will create a version
      await productService.updateProduct(
        productId,
        { optimizations },
        'manual',
        marketplaceKey
      );

      // Also save this as an optimization result for history
      const result: OptimizationResult = {
        productId,
        marketplace: this.denormalizeMarketplace(marketplaceKey),
        content: {
          ...currentContent,
          [field]: value
        },
        prompt: {
          id: 'manual-edit',
          title: 'Manual Edit'
        },
        timestamp: new Date(),
        status: 'success'
      };

      await this.saveOptimizationResult(result);
    } catch (error) {
      
      throw error;
    }
  }

  private generatePrompt(product: any, marketplace: string, promptTemplate: string): string {
    const shortDescription = product.basicInfo.description.length > 1000 
      ? product.basicInfo.description.substring(0, 1000) + '...' 
      : product.basicInfo.description;

    // Extract product specifications from title
    const dimensions = product.basicInfo.title.match(/\d+(?:"|''|inch|in)?/);
    const quantity = product.basicInfo.title.match(/\d+\s*(?:pack|piece|pc|pcs|set)/i);
    
    // Add specifications to the template if marketplace is Shopify
    let finalTemplate = promptTemplate;
    if (marketplace.toLowerCase() === 'shopify') {
      finalTemplate = promptTemplate + `

CRITICAL BULLET POINT REQUIREMENTS:

1. First point: Lead with strongest benefit/feature from original content. Focus on the primary value proposition.
   Example format: "LEAK-PROOF DESIGN FOR YOUR PEACE OF MIND: [Expand on primary benefit with specific details]"

2. Second point: MUST contain ALL product specifications in ONE point (DO NOT split across multiple points):
   - Product Dimensions: ${dimensions ? dimensions[0] : 'Not specified'}
   - Quantity/Pack Size: ${quantity ? quantity[0] : 'Not specified'}
   Format: "PRODUCT SPECIFICATIONS: [dimensions], [quantity] - premium quality product designed for professional use"

3. Third point: Focus on key features and benefits from original content. Maintain original selling points but optimize for clarity.
   Example format: "[FEATURE BENEFIT]: [Detailed explanation with specific use cases]"

4. Fourth point: Focus on unique selling propositions and applications. Use original content's unique features.
   Example format: "PERFECT FOR [specific use case]: [Expand on versatility and applications]"

5. Fifth point: Focus on additional benefits and features from original content.
   Example format: "[FEATURE BENEFIT]: [Detailed explanation with specific advantages]"

6. Sixth point: MUST use this EXACT format (no variations allowed):
   "GUARANTEED FOR LIFE: We offer a No Nonsense Lifetime Satisfaction Guarantee on all of our ${product?.basicInfo?.type || 'products'}. If at any point you're not 100% happy, just send us an email, and we promise to make it right!"

CRITICAL RULES:
- PRESERVE original content's key selling points and unique features
- Include ALL original content's key selling points and unique features
- MATCH or EXCEED original content's length
- MAINTAIN detailed explanations from original content while optimizing for clarity
- DO NOT split specifications across multiple points - ALL specifications MUST be in point 2
- DO NOT modify the lifetime guarantee format in point 5
- DO NOT include any specifications in points 1, 3, 4, or 5
- Points 1, 3, and 4 must expand on benefits with specific details from original content
- Use CAPS for key feature introductions (e.g., "LEAK-PROOF DESIGN", "PERFECT FOR")
`;
    }

    const replacements = {
      '{{title}}': product.basicInfo.title,
      '{{description}}': shortDescription,
      '{{bulletPoints}}': product.basicInfo.bulletPoints.join('\n'),
      '{{categories}}': product.basicInfo.categories.join(', '),
      '{{metaDescription}}': product.basicInfo.metaDescription || 'No meta description available',
      '{{pageTitle}}': product.basicInfo.pageTitle || product.basicInfo.title
    };

    let finalPrompt = finalTemplate;
    for (const [key, value] of Object.entries(replacements)) {
      finalPrompt = finalPrompt.replace(key, value);
    }

    console.log('Generated prompt:', {
      marketplace,
      productId: product.id,
      timestamp: new Date().toISOString(),
      prompt: finalPrompt
    });

    return finalPrompt;
  }

  async getOptimizationHistory(
    productId: string,
    marketplace?: string
  ): Promise<OptimizationResult[]> {
    try {
      let q = query(
        collection(db, 'optimizations'),
        where('productId', '==', productId),
        orderBy('timestamp', 'desc')
      );

      if (marketplace) {
        q = query(q, where('marketplace', '==', marketplace.toLowerCase()));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate()
      })) as OptimizationResult[];
    } catch (error) {
      
      throw error;
    }
  }
}

export const optimizationService = OptimizationService.getInstance();
